import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'

/**
 * Screensaver and inactivity-logout settings, from system settings.
 *
 * Read by every signed-in user, not just admins — the screensaver applies to everyone, so every
 * client needs the numbers. Only writing is admin-gated.
 *
 * The defaults here match the server's, and matter: if the request fails the app must still
 * behave, and it must behave the way it did before this feature existed. 480 minutes is the
 * session window the app already had, so a failed fetch cannot shorten anyone's session.
 */
export interface IdleSettings {
  screensaverEnabled: boolean
  screensaverMinutes: number
  logoutMinutes: number
  limits?: { screensaverMin: number; screensaverMax: number; logoutMin: number; logoutMax: number }
}

export const IDLE_FALLBACK: IdleSettings = {
  screensaverEnabled: true,
  screensaverMinutes: 10,
  logoutMinutes: 480,
}

export function useIdleSettings() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return useQuery<IdleSettings>({
    queryKey: ['idle-settings'],
    enabled: isAuthenticated,
    // Rarely changes, and every client polling it would be noise on a screen left up for days.
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const res = await api.get('/api/admin/idle-settings')
      const body = res.data as { data?: IdleSettings } | IdleSettings
      return ((body as { data?: IdleSettings })?.data ?? body) as IdleSettings
    },
  })
}
